import { Beam, GhostNote, Stave, StaveNote } from 'vexflow';
import type { Renderer } from 'vexflow';
import type { RhythmicCell } from '../rhythmicCellEngine';
import { tokenToLabel } from '../tokens';
import type { RhythmReadingMethod } from '../../types/music';
import type { VexNoteSpec } from './vexFlowMapper';

export const VEX_SLATE_800 = '#1e293b';

export interface VexCellRange {
  start: number;
  end: number;
  beatIndex?: number;
}

type VexCtx = ReturnType<Renderer['getContext']>;

function isBeamableSpec(spec: VexNoteSpec | undefined): boolean {
  if (!spec || spec.isBlank || spec.isRest) return false;
  return spec.duration === '8' || spec.duration === '16';
}

function isBeamableNote(note: StaveNote, spec: VexNoteSpec | undefined): boolean {
  if (!isBeamableSpec(spec)) return false;
  try {
    return !note.isRest();
  } catch {
    return true;
  }
}

/** Una barra de unión por célula rítmica de 1 tiempo (regla determinista del motor de células). */
export function drawBeamsPerCell(
  ctx: VexCtx,
  notes: Array<StaveNote | GhostNote>,
  cellRanges: VexCellRange[],
  specs: VexNoteSpec[],
): void {
  for (const { start, end } of cellRanges) {
    const cellNotes: StaveNote[] = [];
    for (let i = start; i < end; i += 1) {
      const note = notes[i];
      if (!(note instanceof StaveNote)) continue;
      if (isBeamableNote(note, specs[i])) cellNotes.push(note);
    }
    if (cellNotes.length >= 2) {
      try {
        new Beam(cellNotes).setContext(ctx).draw();
      } catch {
        /* grupo inválido para VexFlow */
      }
    }
  }
}

/** Beaming legacy por flags beamWithNext (patrones planos sin células). */
export function drawBeamsFromSpecs(
  ctx: VexCtx,
  notes: Array<StaveNote | GhostNote>,
  specs: VexNoteSpec[],
): void {
  let group: StaveNote[] = [];
  for (let i = 0; i < specs.length; i += 1) {
    const spec = specs[i]!;
    const note = notes[i];
    if (!(note instanceof StaveNote) || spec.isBlank) continue;

    if (isBeamableNote(note, spec)) {
      group.push(note);
      if (!spec.beamWithNext) {
        if (group.length >= 2) {
          try {
            new Beam(group).setContext(ctx).draw();
          } catch {
            /* grupo inválido */
          }
        }
        group = [];
      }
    } else if (group.length >= 2) {
      try {
        new Beam(group).setContext(ctx).draw();
      } catch {
        /* grupo inválido */
      }
      group = [];
    } else {
      group = [];
    }
  }
  if (group.length >= 2) {
    try {
      new Beam(group).setContext(ctx).draw();
    } catch {
      /* grupo inválido */
    }
  }
}

export function drawBeamsForPattern(
  ctx: VexCtx,
  notes: Array<StaveNote | GhostNote>,
  specs: VexNoteSpec[],
  cellRanges?: VexCellRange[],
): void {
  if (cellRanges?.length) {
    drawBeamsPerCell(ctx, notes, cellRanges, specs);
    return;
  }
  drawBeamsFromSpecs(ctx, notes, specs);
}

function cellCenterX(stave: Stave, notes: Array<StaveNote | GhostNote>, start: number, end: number): number {
  const xs: number[] = [];
  for (let i = start; i < end; i += 1) {
    const note = notes[i];
    if (!note || !('getAbsoluteX' in note) || typeof note.getAbsoluteX !== 'function') continue;
    try {
      xs.push(note.getAbsoluteX() + 6);
    } catch {
      /* fallback */
    }
  }
  if (xs.length) return xs.reduce((sum, x) => sum + x, 0) / xs.length;

  const noteStart = stave.getNoteStartX();
  const noteEnd = stave.getNoteEndX();
  const slotWidth = (noteEnd - noteStart) / Math.max(end - start, 1);
  return noteStart + ((start + end) / 2) * slotWidth;
}

function syllableForCell(
  cell: RhythmicCell,
  beatIndex: number,
  missingBeatIndex: number | undefined,
  readingMethod: RhythmReadingMethod,
): string {
  if (beatIndex === missingBeatIndex) return '?';
  const label = cell.takadimi?.trim();
  if (label) return label;
  const token = cell.tokens[0];
  return token ? tokenToLabel(token, readingMethod) : '';
}

/** Sílabas Takadimi/Kodály centradas bajo cada célula — evita amontonamiento HTML. */
export function drawBeatCellReadingLabels(
  ctx: VexCtx,
  stave: Stave,
  notes: Array<StaveNote | GhostNote>,
  cellRanges: VexCellRange[],
  beatCells: RhythmicCell[],
  readingMethod: RhythmReadingMethod,
  printMode: boolean,
  missingBeatIndex?: number,
): void {
  const ink = printMode ? VEX_SLATE_800 : 'rgba(244, 240, 232, 0.92)';

  for (const range of cellRanges) {
    const beatIndex = range.beatIndex ?? 0;
    const cell = beatCells[beatIndex];
    if (!cell || cell.modifiers?.isSpanContinuation || cell.modifiers?.isPadding) continue;

    const text = syllableForCell(cell, beatIndex, missingBeatIndex, readingMethod);
    if (!text) continue;

    const x = cellCenterX(stave, notes, range.start, range.end);
    const y = stave.getYForLine(4) + (printMode ? 30 : 36);
    ctx.setFont('Arial', printMode ? 10 : 11, 'normal');
    ctx.setFillStyle(ink);
    const width = ctx.measureText(text).width;
    ctx.fillText(text, x - width / 2, y);
  }
}

/** Casillero incógnita — GhostNote para espaciado + marco neo-swiss. */
export function createBlankGhostNote(spec: VexNoteSpec): GhostNote {
  return new GhostNote({ duration: spec.duration || 'q' });
}

export function drawBlankGhostSlot(
  ctx: VexCtx,
  stave: Stave,
  note: StaveNote | GhostNote,
  printMode: boolean,
): void {
  let x = stave.getNoteStartX() + 12;
  const width = 36;
  try {
    if ('getAbsoluteX' in note && typeof note.getAbsoluteX === 'function') {
      x = note.getAbsoluteX() - 10;
    }
  } catch {
    /* fallback */
  }

  const top = stave.getYForLine(0) - 10;
  const height = stave.getYForLine(4) - stave.getYForLine(0) + 20;
  const stroke = printMode ? VEX_SLATE_800 : '#64748b';

  ctx.setFillStyle(printMode ? '#ffffff' : 'rgba(248, 244, 236, 0.96)');
  ctx.setStrokeStyle(stroke);
  ctx.setLineWidth(1.5);
  ctx.beginPath();
  ctx.rect(x, top, width, height);
  ctx.fill();
  ctx.stroke();
}
